/**
 * THE SESSION TOKEN, AND THE ONLY PLACE IT IS KEPT.
 *
 * Rachel's decision, 18-09-2026: this repo authenticates with a header token
 * (`X-Auth-Token`) across the whole API. That REVERSES docs/BACKEND_HANDOFF.md
 * §4a, which argued for an HttpOnly cookie and said this repo had no token store
 * deliberately. `docs/DECISIONS.md` records the reversal and what it costs.
 *
 * SESSION STORAGE, NOT LOCAL STORAGE. A counter terminal is shared and left
 * open. sessionStorage survives the reload an operator does and dies with the
 * tab that ends the shift; localStorage would outlive both, and a token that
 * outlives the shift is the failure mode worth designing against. The token is
 * a credential, not healthcare data, so it is the per-terminal state CLAUDE.md
 * allows. Storage that throws — a private window — falls back to memory, and
 * the token then lasts for this page only.
 *
 * TWO TOKENS, ONE HEADER. `POST /v1/user/login` returns an INTERIM token, good
 * only for choosing a branch and for resetting an expired password.
 * `POST /v1/user/login/branches/{branchCode}` exchanges it for the working one.
 * The document gives them the same name and the same header, so the STAGE is
 * recorded beside the token rather than inferred: nothing has to guess whether
 * the holder has chosen a branch yet.
 */
export type TokenStage = "interim" | "active";

export interface StoredToken {
  readonly token: string;
  readonly stage: TokenStage;
}

const KEY = "pharmos.auth.token";

/** The fallback when sessionStorage is unavailable or throws. */
let memory: StoredToken | null = null;

function store(): Storage | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

function isStoredToken(value: unknown): value is StoredToken {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<StoredToken>;
  return (
    typeof candidate.token === "string" &&
    candidate.token !== "" &&
    (candidate.stage === "interim" || candidate.stage === "active")
  );
}

export function readToken(): StoredToken | null {
  const storage = store();
  if (!storage) return memory;
  try {
    const raw = storage.getItem(KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return isStoredToken(parsed) ? parsed : null;
  } catch {
    return memory;
  }
}

export function writeToken(token: string, stage: TokenStage): void {
  memory = { token, stage };
  try {
    store()?.setItem(KEY, JSON.stringify(memory));
  } catch {
    // Memory only: the token lasts for this page.
  }
}

export function clearToken(): void {
  memory = null;
  try {
    store()?.removeItem(KEY);
  } catch {
    // Memory only.
  }
}

/** True once a branch has been chosen — the only stage the rest of the API accepts. */
export function hasActiveToken(): boolean {
  return readToken()?.stage === "active";
}

/** The value for the `X-Auth-Token` header, at whichever stage it is. */
export function authHeaderValue(): string | null {
  return readToken()?.token ?? null;
}
